import { useEffect, useRef } from 'react';
import usePage from '@discover-data/stores/hooks/page/usePage';
import { NEXT_PUBLIC_DISCOVER_PAGE_SIZE } from '@/lib/env';

export default function useInfiniteScroll(
  observedRestaurantId: number | undefined,
  loading: boolean
) {
  const { nextPage, hasNextPage } = usePage();
  const observedItemRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const currentObservedItemRef = observedItemRef.current;

    // fetch more results when the observed list item is in the viewport
    const onIntersect = (entries: IntersectionObserverEntry[]) => {
      const isIntersecting = entries.some((entry) => entry.isIntersecting); // when at least one element is in view

      if (isIntersecting && !loading && hasNextPage) {
        nextPage();
      }
    };

    const bottomMargin = (NEXT_PUBLIC_DISCOVER_PAGE_SIZE / 4) * 500; // one restaurant card is about 500px high

    const intersectionObserver = new IntersectionObserver(onIntersect, {
      root: null, // default to the browser viewport
      rootMargin: `0px 0px ${bottomMargin}px 0px`, // add a margin to the bottom of the viewport to trigger the intersection when the observed item is X px below the viewport
      threshold: 0,
    });

    if (currentObservedItemRef) {
      intersectionObserver.observe(currentObservedItemRef);
    }

    return () => {
      if (currentObservedItemRef) {
        intersectionObserver.unobserve(currentObservedItemRef);
      }
    };
  }, [
    observedRestaurantId, // make sure to re-attach the intersection observer when the observed restaurant changes
    observedItemRef,
    hasNextPage,
    loading,
    nextPage,
  ]);

  return observedItemRef;
}
